/** @file One batch (one undo step) that stores a topology plan on a rigid or skinned mesh. */
import { meshOffsets } from '@pose2d/commands/animation/meshOffsets';
import type { CommandCall, Doc } from '@pose2d/format/types';
import { clamp } from '@pose2d/math/clamp';
import type { MeshTarget } from '../meshTarget';
import { blendSources, type TopologyPlan } from './blendSources';
import { deformRewrites } from './deformRewrites';
import { shapeCalls } from './shapeCalls';
import { skinnedRoundTrip } from './skinnedRoundTrip';

/**
 * Only triangles/hull/edges when every vertex stays; a skinned mesh whose vertex count changes (or gains a blended
 * vertex) goes through skinnedRoundTrip; otherwise positions + uvs (+ reordered weights), shape, then deform keys.
 * A shrinking plan stores the shape first (its indices fit both counts), so mesh.setVertices drops no triangles.
 */
export function topologyCalls(doc: Doc, target: MeshTarget, plan: TopologyPlan): CommandCall[] {
  const { attachment, mesh } = target;
  const g = mesh.geometry;
  const oldCount = mesh.uvs.length / 2;
  if (blendSources.isIdentity(plan.sources, oldCount)) return shapeCalls(attachment, mesh, plan, false);
  const uvs = blendSources.pairs(mesh.uvs, plan.sources).map((u) => clamp(u, 0, 1));
  const reorder = plan.sources.length === oldCount && plan.sources.every((s) => s.kind === 'old');
  if (g.kind === 'skinned' && !reorder) return skinnedRoundTrip(doc, target, plan, uvs);
  const shape = shapeCalls(attachment, mesh, plan, true);
  const shrink = plan.sources.length < oldCount;
  const calls: CommandCall[] = [...(shrink ? shape : []), { name: 'mesh.setVertices', input: { target: attachment, vertices: blendSources.pairs(meshOffsets.rest(mesh), plan.sources), uvs } }];
  if (g.kind === 'skinned') calls.push({ name: 'mesh.setWeights', input: { target: attachment, weights: blendSources.rows(g.weights, plan.sources) } });
  return [...calls, ...(shrink ? [] : shape), ...deformRewrites(doc, attachment, plan, oldCount, null)];
}
